import React from "react";
import { StructuredText } from "react-datocms";
import classNames from "classnames";
import { isEmptyDocument } from "datocms-structured-text-utils";
import { ProductInterval } from "@/api";
import { DownChevronIcon, InfoTooltip } from "@/components";
import { CheckIcon } from "@/components";
import { EXTERNAL, SELECT_PLAN_SLUGS } from "@/config";
import { useToggleOverflow } from "@/modules/select-plan-comparison/hooks";
import { PlanBlockWithStripeData, SelectPlanWithStripeData } from "@/modules/select-plan-comparison/utils";
import styles from "@/styles/custom.module.css";
import { numberToCurrency, createUrl } from "@/utils";
import { AnchorButton, Button } from "@narrative-software/narrative-web-ui";
import { getPlanSpecialType } from "../BlackFriday/BlackFridayPlanComparison";

type ComparisonPlanProps = {
  position: number;
  billingInterval: ProductInterval;
  plan: PlanBlockWithStripeData;
};

const ComparisonPlan = ({ position, billingInterval, plan }: ComparisonPlanProps) => {
  const {
    // presentational data (for this page/instance only)
    showRainbowBar,
    ctaDisabled,
    showCouponSavings,
    ctaLabel,
    ctaAppearance,
    // plan data (all pages)
    planData: { planName, description, features, stripePlanSlug, yearlyPrice, monthlyPrice },
    stripeCoupon,
  } = plan;
  const {
    hasDiscount,
    discountedPrice,
    discountedPriceDollars,
    discountedPriceCents,
    customDiscountBanner,
    yearlySavings,
  } = getCalculatedDiscount(billingInterval, plan);
  const special = getPlanSpecialType(plan.planData);

  const { ref: overflowRef, ...featureOverflow } = useToggleOverflow();

  return (
    <div
      style={{ width: "100%" }}
      id={`${stripePlanSlug}`}
      className={classNames("md:col-span-5 w-full flex flex-col lg:flex-1 relative", styles["scroll-snap-child"], {
        "bg-stealth-bomber text-white": !!special,
        "bg-white": !special,
        "md:col-start-2": position === 0,
        "bg-gradient-to-r from-[#31256F] to-[#FCB76F] via-[#B44893]": !special,
        "lifetime-box-gradient-select": special === "bf-select",
        "bg-slowpoke": special === "bf-publish",

        // Reveal the gradient background
        "px-[1px] pb-[1px] pt-2": showRainbowBar && !special,
        "p-[2px]": showRainbowBar || special,
        "border-1": !showRainbowBar && !special,
      })}
    >
      {hasDiscount && !customDiscountBanner && showCouponSavings && (
        <div className="absolute right-0 top-0 bg-[#FFDA18] text-stealth-bomber py-2 px-4 rounded-bl-lg font-semibold font-18">
          Save <span className="font-light">$</span>
          {yearlySavings}
        </div>
      )}

      {customDiscountBanner && (
        <div className="absolute right-0 top-0 bg-[#FFDA18] text-stealth-bomber py-2 px-4 rounded-bl-lg font-semibold font-18">
          {customDiscountBanner}
        </div>
      )}

      <div
        className={classNames("bg-white pb-10 md:pb-13 flex flex-col h-full", {
          "pt-[29px]": showRainbowBar,
          "pt-9": !showRainbowBar,
          "select-plan-background-gradient": !!special,
          "bg-white": !special,
        })}
      >
        {/* Header */}
        <div
          className={classNames("flex flex-col items-center px-10 pt-6 pb-6 border-b-1 text-center min-h-[210px]", {
            "border-gumboot": !!special,
          })}
        >
          <h3 className={classNames("relative mb-2.5 text-h3 leading-none md:text-34 font-semibold")}>{planName}</h3>
          <div className="relative text-6xl font-semibold leading-none">
            <sup className="absolute mt-7 -ml-4 text-2xl font-light">$</sup>
            {hasDiscount ? (
              discountedPriceCents === "00" ? (
                discountedPrice
              ) : (
                <>
                  <span className="tracking-[-5px]">{discountedPriceDollars}</span>
                  <span className="text-34">.{discountedPriceCents}</span>
                </>
              )
            ) : (
              numberToCurrency(billingInterval === ProductInterval.Month ? monthlyPrice : yearlyPrice, true)
            )}
            {hasDiscount && showCouponSavings && (
              <div className="absolute bottom-[15%] w-[100px] left-[95%]">
                <p className="text-shadyLady text-[15px]">
                  usually{" "}
                  <span className="strikethrough">
                    {numberToCurrency(billingInterval === ProductInterval.Month ? monthlyPrice : yearlyPrice)}
                  </span>
                </p>
              </div>
            )}
          </div>
          <div className="mt-1 leading-relaxed">
            {isEmptyDocument(description) ? (
              <>
                USD per month, billed&nbsp;
                {billingInterval === ProductInterval.Month ? "monthly" : "yearly"}
              </>
            ) : (
              <StructuredText data={description} />
            )}
          </div>
        </div>
        {/* End Header */}
        {/* Features */}
        <div className="px-4 md:px-8 pt-8 relative">
          <div
            ref={overflowRef}
            className={classNames("transition-[max-height] overflow-hidden", {
              "max-h-[440px] md:max-h-[310px]": !featureOverflow.isOpen,
              "max-h-[600px]": featureOverflow.isOpen,
            })}
          >
            <ul>
              {features.map((feature) => (
                <li key={feature.id} className="text-sm flex items-center justify-between mb-2">
                  <div className="flex items-center md:mr-5">
                    <div
                      className={classNames("mr-5", {
                        "text-slowpoke": special === "bf-publish",
                        "text-gengar": special !== "bf-publish",
                      })}
                    >
                      <CheckIcon />
                    </div>
                    <StructuredText data={feature.name} />
                  </div>
                  {feature.description && <InfoTooltip description={feature.description} special={special} />}
                </li>
              ))}
            </ul>
          </div>
          {featureOverflow.hasOverflow && (
            <>
              {!featureOverflow.isOpen && !special && (
                <div className="h-[66px] w-full left-0 bottom-[30px] bg-gradient-to-t from-white to-transparent absolute"></div>
              )}
              <button
                className="text-sm py-2 flex items-center w-[60px] justify-between"
                onClick={() => featureOverflow.toggle()}
              >
                {featureOverflow.isOpen ? "Less" : "More"}
                <DownChevronIcon
                  className={classNames("origin-center", {
                    "rotate-180": featureOverflow.isOpen,
                  })}
                />
              </button>
            </>
          )}
        </div>
        {/* End Features */}
        {/* CTA */}
        <div className="px-4 md:px-8 mt-auto">
          {ctaDisabled ? (
            <Button appearance="primary" colour="gray-600" disabled className="text-sm normal-case">
              {ctaLabel}
            </Button>
          ) : (
            <AnchorButton
              href={getCtaButtonHref(stripeCoupon?.code || "", plan.planData, billingInterval)}
              appearance={ctaAppearance}
              colour="gray-600"
              className={classNames("text-sm normal-case", {
                "lifetime-background-gradient-publish border-none !text-white": special === "bf-publish",
                "lifetime-background-gradient-select border-none !text-white": special === "bf-select",
              })}
            >
              {ctaLabel}
            </AnchorButton>
          )}
        </div>
        {/* End CTA */}
      </div>
    </div>
  );
};

/**
 * Coupon Discounts can be displayed individually at the page level or universally at the plan level. If both are
 * applied, currently we prefer the page level coupon.
 */
function getCalculatedDiscount(
  billingInterval: ProductInterval,
  {
    customCouponBanner,
    stripeCoupon,
    planData: {
      stripeCouponDiscount,
      stripeCouponCode,
      stripeCouponAppliesMonthly,
      stripeCouponAppliesYearly,
      monthlyPrice,
      yearlyPrice,
    },
  }: PlanBlockWithStripeData,
): {
  hasDiscount: boolean;
  yearlySavings?: string;
  discountedPrice?: string;
  discountedPriceDollars?: string;
  discountedPriceCents?: string;
  customDiscountBanner?: string;
} {
  const hasPageLevelCoupon = stripeCoupon && stripeCoupon.discount;

  const { discount, appliesMonthly, appliesYearly } = (hasPageLevelCoupon && stripeCoupon) || {
    discount: stripeCouponDiscount,
    appliesMonthly: stripeCouponAppliesMonthly,
    appliesYearly: stripeCouponAppliesYearly,
  };

  if (!discount) {
    return {
      hasDiscount: false,
    };
  }

  if (billingInterval === ProductInterval.Month && !appliesMonthly) {
    return {
      hasDiscount: false,
    };
  }

  if (billingInterval === ProductInterval.Year && !appliesYearly) {
    return {
      hasDiscount: false,
    };
  }

  const priceWithDiscountDollars =
    (billingInterval === ProductInterval.Month ? monthlyPrice : yearlyPrice) * (1 - discount);

  const [dollars, cents] = (priceWithDiscountDollars / 100).toFixed(2).split(".");
  const yearlySavings = Intl.NumberFormat("en").format((yearlyPrice * discount * 12) / 100);

  return {
    hasDiscount: true,
    discountedPrice: numberToCurrency(priceWithDiscountDollars, true),
    discountedPriceDollars: dollars,
    discountedPriceCents: cents,
    yearlySavings,
    customDiscountBanner: (hasPageLevelCoupon ? customCouponBanner : undefined)?.replace(
      "{yearlySavings}",
      String(yearlySavings),
    ),
  };
}

/**
 * Send the user to sign up with specific plan details.
 */
function getCtaButtonHref(couponCode: string, planData: SelectPlanWithStripeData, interval: ProductInterval) {
  if (SELECT_PLAN_SLUGS.defaultPro === planData.stripePlanSlug) {
    return createUrl(EXTERNAL.SIGN_UP.HREF, {
      interval,
      product: "select",
      backPath: "/checkout",
      planID: planData.stripePlanId,
      fp_ref: couponCode,
    });
  }

  return createUrl(EXTERNAL.SELECT.GET_STARTED.HREF, { fp_ref: couponCode });
}

export { ComparisonPlan };
